"use client";

import React, { useState, useEffect, useRef } from "react";
import { Input } from "../common/input";
import { PasswordInput } from "./PasswordInput";

export type LoginMethod = "password" | "email-code";

export interface LoginPageProps {
  /** Portal title displayed below the logo */
  title: string;
  /** Description text below the title */
  description: string;
  /** Theme color for accents */
  theme?: "teal" | "violet" | "blue" | "green" | "amber" | "rose";
  /** Logo component or image */
  logo?: React.ReactNode | null;
  /** Logo URL if not providing a custom logo component */
  logoUrl?: string;
  /** Logo alt text */
  logoAlt?: string;
  /** Called when form is submitted with email and password */
  onSubmit: (email: string, password: string) => Promise<void>;
  /** Initial error message to display (e.g., from URL params) */
  initialError?: string | null;
  /** Initial email to pre-fill (e.g., from invite link) */
  initialEmail?: string | null;
  /** localStorage key for remembering email */
  rememberEmailKey?: string;
  /** Forgot password link URL */
  forgotPasswordUrl?: string;
  /** Support contact info */
  supportEmail?: string;
  /** Additional footer content */
  footerContent?: React.ReactNode | null;
  /** Email input placeholder */
  emailPlaceholder?: string;
  /** Password input placeholder */
  passwordPlaceholder?: string;
  /** Show trust indicators at bottom */
  showTrustIndicators?: boolean;
  /** Enable email code login option */
  enableEmailCode?: boolean;
  /** Called to send verification code to email */
  onSendCode?: (email: string) => Promise<void>;
  /** Called to verify the code and log in */
  onVerifyCode?: (email: string, code: string) => Promise<void>;
}

const themeConfig = {
  teal: {
    gradient: "from-[#f0f9ff] via-white to-[#fef5ee]",
    decorative1: "bg-primary-200",
    decorative2: "bg-secondary-100",
    button: "bg-primary-500 hover:bg-primary-600 focus:ring-primary-500",
    buttonShadow: "shadow-primary-500/20 hover:shadow-primary-500/30",
    checkbox: "text-primary-500 focus:ring-primary-500",
    link: "text-primary-500 hover:text-primary-600",
    accent: "bg-primary-500",
    ring: "ring-primary-100",
  },
  violet: {
    gradient: "from-violet-50 via-white to-purple-50",
    decorative1: "bg-violet-200",
    decorative2: "bg-purple-200",
    button: "bg-violet-600 hover:bg-violet-700 focus:ring-violet-500",
    buttonShadow: "shadow-violet-500/20 hover:shadow-violet-500/30",
    checkbox: "text-violet-600 focus:ring-violet-500",
    link: "text-violet-600 hover:text-violet-500",
    accent: "bg-violet-600",
    ring: "ring-violet-100",
  },
  blue: {
    gradient: "from-blue-50 via-white to-indigo-50",
    decorative1: "bg-blue-200",
    decorative2: "bg-indigo-200",
    button: "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500",
    buttonShadow: "shadow-blue-500/20 hover:shadow-blue-500/30",
    checkbox: "text-blue-600 focus:ring-blue-500",
    link: "text-blue-600 hover:text-blue-500",
    accent: "bg-blue-600",
    ring: "ring-blue-100",
  },
  green: {
    gradient: "from-green-50 via-white to-emerald-50",
    decorative1: "bg-green-200",
    decorative2: "bg-emerald-200",
    button: "bg-green-600 hover:bg-green-700 focus:ring-green-500",
    buttonShadow: "shadow-green-500/20 hover:shadow-green-500/30",
    checkbox: "text-green-600 focus:ring-green-500",
    link: "text-green-600 hover:text-green-500",
    accent: "bg-green-600",
    ring: "ring-green-100",
  },
  amber: {
    gradient: "from-amber-50 via-white to-orange-50",
    decorative1: "bg-amber-200",
    decorative2: "bg-orange-200",
    button: "bg-amber-600 hover:bg-amber-700 focus:ring-amber-500",
    buttonShadow: "shadow-amber-500/20 hover:shadow-amber-500/30",
    checkbox: "text-amber-600 focus:ring-amber-500",
    link: "text-amber-600 hover:text-amber-500",
    accent: "bg-amber-600",
    ring: "ring-amber-100",
  },
  rose: {
    gradient: "from-rose-50 via-white to-pink-50",
    decorative1: "bg-rose-200",
    decorative2: "bg-pink-200",
    button: "bg-rose-600 hover:bg-rose-700 focus:ring-rose-500",
    buttonShadow: "shadow-rose-500/20 hover:shadow-rose-500/30",
    checkbox: "text-rose-600 focus:ring-rose-500",
    link: "text-rose-600 hover:text-rose-500",
    accent: "bg-rose-600",
    ring: "ring-rose-100",
  },
};

export function LoginPage({
  title,
  description,
  theme = "teal",
  logo,
  logoUrl = "https://cdn.prod.website-files.com/65154e68ae1ad53c6e8aaa1a/6516a44d8f77816b807f929d_Logo.svg",
  logoAlt = "Alli",
  onSubmit,
  initialError = null,
  initialEmail = null,
  rememberEmailKey = "remembered-email",
  forgotPasswordUrl = "/forgot-password",
  supportEmail,
  footerContent,
  emailPlaceholder = "you@company.com",
  passwordPlaceholder = "Enter your password",
  showTrustIndicators = true,
  enableEmailCode = false,
  onSendCode,
  onVerifyCode,
}: LoginPageProps) {
  const colors = themeConfig[theme];

  // Initial state must NOT read localStorage — SSR runs on the server
  // (window undefined → "") while hydration runs on the client (window
  // defined → possibly a stored email). That mismatch triggers React's
  // "server-rendered HTML didn't match the client" hydration error. Seed
  // with the server-safe value and hydrate localStorage in useEffect.
  const [email, setEmail] = useState(initialEmail ?? "");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem(rememberEmailKey);
    // initialEmail (e.g., from an invite link) wins over the stored value
    // for the email input; the checkbox tracks whether anything is stored.
    if (!initialEmail && stored) setEmail(stored);
    setRememberMe(!!stored);
  }, [initialEmail, rememberEmailKey]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(initialError);

  // Email code login state
  const [loginMethod, setLoginMethod] = useState<LoginMethod>("password");
  const [codeSent, setCodeSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (initialError) {
      setError(initialError);
    }
  }, [initialError]);

  // Cleanup cooldown timer
  useEffect(() => {
    return () => {
      if (cooldownRef.current) {
        clearInterval(cooldownRef.current);
      }
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      if (typeof window !== "undefined") {
        if (rememberMe) {
          localStorage.setItem(rememberEmailKey, email);
        } else {
          localStorage.removeItem(rememberEmailKey);
        }
      }

      await onSubmit(email, password);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
      setIsLoading(false);
    }
  };

  const handleSendCode = async () => {
    if (!email) {
      setError("Please enter your email address.");
      return;
    }

    if (!onSendCode) {
      setError("Email code login is not configured.");
      return;
    }

    setError(null);
    setIsSendingCode(true);

    try {
      await onSendCode(email);
      setCodeSent(true);
      // Start 60 second cooldown
      setResendCooldown(60);
      cooldownRef.current = setInterval(() => {
        setResendCooldown(prev => {
          if (prev <= 1) {
            if (cooldownRef.current) {
              clearInterval(cooldownRef.current);
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Failed to send verification code. Please try again.");
      }
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!verificationCode || verificationCode.length !== 6) {
      setError("Please enter the 6-digit verification code.");
      return;
    }

    if (!onVerifyCode) {
      setError("Email code login is not configured.");
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      if (typeof window !== "undefined") {
        if (rememberMe) {
          localStorage.setItem(rememberEmailKey, email);
        } else {
          localStorage.removeItem(rememberEmailKey);
        }
      }

      await onVerifyCode(email, verificationCode);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Verification failed. Please try again.");
      }
      setIsLoading(false);
    }
  };

  const switchLoginMethod = (method: LoginMethod) => {
    setLoginMethod(method);
    setError(null);
    setCodeSent(false);
    setVerificationCode("");
    setPassword("");
  };

  return (
    <div className={`min-h-screen flex flex-col bg-gradient-to-br ${colors.gradient}`}>
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute -top-40 -right-40 w-[500px] h-[500px] ${colors.decorative1} rounded-full blur-3xl opacity-20`} />
        <div className={`absolute -bottom-40 -left-40 w-[500px] h-[500px] ${colors.decorative2} rounded-full blur-3xl opacity-30`} />
        <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] ${colors.ring} rounded-full opacity-50`} />
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          {/* Logo */}
          <div className="flex justify-center">
            {logo || (
              <img
                src={logoUrl}
                alt={logoAlt}
                className="h-20 w-auto"
              />
            )}
          </div>

          {/* Title & Description */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
              {title}
            </h1>
            <p className="mt-2 text-gray-600">
              {description}
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-200/50 border border-white/60 p-8 sm:p-10">
            {/* Error Alert */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-2xl flex items-start gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mt-0.5">
                  <svg className="w-3 h-3 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Login method toggle */}
            {enableEmailCode && (
              <div className="flex rounded-xl bg-gray-100 p-1 mb-6">
                <button
                  type="button"
                  onClick={() => switchLoginMethod("password")}
                  className={`flex-1 py-2 px-4 text-sm font-medium rounded-lg transition-all ${
                    loginMethod === "password"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  Password
                </button>
                <button
                  type="button"
                  onClick={() => switchLoginMethod("email-code")}
                  className={`flex-1 py-2 px-4 text-sm font-medium rounded-lg transition-all ${
                    loginMethod === "email-code"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  Email Code
                </button>
              </div>
            )}

            {/* Password login form */}
            {loginMethod === "password" && (
              <form className="space-y-5" onSubmit={handleSubmit}>
                <Input
                  label="Email address"
                  type="email"
                  id="email"
                  name="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={emailPlaceholder}
                  className="rounded-xl"
                />

                <PasswordInput
                  label="Password"
                  value={password}
                  onChange={setPassword}
                  placeholder={passwordPlaceholder}
                  id="password"
                  className="rounded-xl"
                />

                <div className="flex items-center justify-between">
                  <label className="flex items-center cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setRememberMe(checked);
                        if (!checked && typeof window !== "undefined") {
                          localStorage.removeItem(rememberEmailKey);
                        }
                      }}
                      className={`h-4 w-4 ${colors.checkbox} border-gray-300 rounded transition-colors cursor-pointer`}
                    />
                    <span className="ml-2 text-sm text-gray-600 group-hover:text-gray-800 transition-colors select-none">
                      Remember me
                    </span>
                  </label>

                  <a
                    href={forgotPasswordUrl}
                    className={`text-sm font-medium ${colors.link} transition-colors`}
                  >
                    Forgot password?
                  </a>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className={`
                    w-full inline-flex items-center justify-center
                    px-6 py-3.5 text-base font-semibold text-white
                    rounded-full shadow-lg ${colors.buttonShadow}
                    transition-all duration-200
                    disabled:opacity-50 disabled:cursor-not-allowed
                    ${colors.button}
                    focus:outline-none focus:ring-2 focus:ring-offset-2
                    transform hover:scale-[1.02] active:scale-[0.98]
                  `}
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Signing in...
                    </span>
                  ) : (
                    <>
                      Sign in
                      <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </>
                  )}
                </button>
              </form>
            )}

            {/* Email code login form */}
            {loginMethod === "email-code" && (
              <div className="space-y-5">
                {!codeSent ? (
                  /* Step 1: Enter email and send code */
                  <div className="space-y-5">
                    <Input
                      label="Email address"
                      type="email"
                      id="email-code"
                      name="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={emailPlaceholder}
                      className="rounded-xl"
                    />

                    <p className="text-sm text-gray-500">
                      We'll send a 6-digit verification code to your email.
                    </p>

                    <button
                      type="button"
                      onClick={handleSendCode}
                      disabled={isSendingCode || !email}
                      className={`
                        w-full inline-flex items-center justify-center
                        px-6 py-3.5 text-base font-semibold text-white
                        rounded-full shadow-lg ${colors.buttonShadow}
                        transition-all duration-200
                        disabled:opacity-50 disabled:cursor-not-allowed
                        ${colors.button}
                        focus:outline-none focus:ring-2 focus:ring-offset-2
                        transform hover:scale-[1.02] active:scale-[0.98]
                      `}
                    >
                      {isSendingCode ? (
                        <span className="flex items-center justify-center">
                          <svg
                            className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                          Sending code...
                        </span>
                      ) : (
                        <>
                          Send verification code
                          <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </>
                      )}
                    </button>
                  </div>
                ) : (
                  /* Step 2: Enter verification code */
                  <form className="space-y-5" onSubmit={handleVerifyCode}>
                    <div className="text-center mb-4">
                      <div className="w-12 h-12 mx-auto mb-3 bg-green-100 rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <p className="text-sm text-gray-600">
                        We sent a code to <span className="font-medium">{email}</span>
                      </p>
                    </div>

                    <Input
                      label="Verification code"
                      type="text"
                      id="verification-code"
                      name="code"
                      autoComplete="one-time-code"
                      inputMode="numeric"
                      pattern="[0-9]{6}"
                      maxLength={6}
                      required
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="Enter 6-digit code"
                      className="rounded-xl text-center text-xl tracking-widest"
                    />

                    <div className="flex items-center justify-between">
                      <label className="flex items-center cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={rememberMe}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setRememberMe(checked);
                            if (!checked && typeof window !== "undefined") {
                              localStorage.removeItem(rememberEmailKey);
                            }
                          }}
                          className={`h-4 w-4 ${colors.checkbox} border-gray-300 rounded transition-colors cursor-pointer`}
                        />
                        <span className="ml-2 text-sm text-gray-600 group-hover:text-gray-800 transition-colors select-none">
                          Remember me
                        </span>
                      </label>

                      <button
                        type="button"
                        onClick={handleSendCode}
                        disabled={resendCooldown > 0 || isSendingCode}
                        className={`text-sm font-medium ${colors.link} transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
                      </button>
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading || verificationCode.length !== 6}
                      className={`
                        w-full inline-flex items-center justify-center
                        px-6 py-3.5 text-base font-semibold text-white
                        rounded-full shadow-lg ${colors.buttonShadow}
                        transition-all duration-200
                        disabled:opacity-50 disabled:cursor-not-allowed
                        ${colors.button}
                        focus:outline-none focus:ring-2 focus:ring-offset-2
                        transform hover:scale-[1.02] active:scale-[0.98]
                      `}
                    >
                      {isLoading ? (
                        <span className="flex items-center justify-center">
                          <svg
                            className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                          Verifying...
                        </span>
                      ) : (
                        <>
                          Verify and sign in
                          <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                          </svg>
                        </>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={() => setCodeSent(false)}
                      className="w-full text-sm text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      Use a different email
                    </button>
                  </form>
                )}
              </div>
            )}

            {/* Support footer */}
            {footerContent || (supportEmail && (
              <div className="mt-8 pt-6 border-t border-gray-100">
                <p className="text-center text-sm text-gray-500">
                  Need help?{" "}
                  <a
                    href={`mailto:${supportEmail}`}
                    className={`font-medium ${colors.link} transition-colors`}
                  >
                    Contact support
                  </a>
                </p>
              </div>
            ))}
          </div>

          {/* Trust indicators */}
          {showTrustIndicators && (
            <div className="mt-8 flex flex-col items-center gap-4">
              <div className="flex items-center gap-6 text-gray-400">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <span className="text-xs">256-bit SSL</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <span className="text-xs">PHIPA Compliant</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                  <span className="text-xs">Trusted Care</span>
                </div>
              </div>
              <p className="text-xs text-gray-400">
                Your privacy and security are our top priority
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
